/**
 * Approval action page — the destination of every magic link in an approval email.
 *
 * Deliberately unauthenticated at the React level: the signed token in the URL is the
 * credential, so a partner can approve from a phone without an SSO round-trip. Validity
 * is decided entirely server-side by functions/src/auth/middleware.js; this page only
 * renders whatever the API says.
 */

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE, auth } from '../firebase';
import { attachmentCategoriesForApproval } from '../lib/approvalAttachments';

/**
 * If the visitor happens to already be signed in we forward their ID token too. The
 * server's policy (auth/middleware.js ACTIVE_POLICY) decides whether a session alone is
 * sufficient; the client never assumes it is. This is what makes the in-app
 * "My Approvals" queue work without a token in the URL.
 */
async function postJson(path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const idToken = await auth.currentUser?.getIdToken().catch(() => null);
  if (idToken) headers.Authorization = `Bearer ${idToken}`;
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  return { res, body: await res.json() };
}

// 'reject' is handled separately below (see rejectChoiceOpen) because it may expand
// into a two-option choice (reject to requestor vs. reject to last approver) instead of
// deciding immediately.
const ACTIONS = [
  { key: 'approve', label: 'Approve', tone: 'bg-emerald-600 dark:bg-emerald-500 hover:bg-emerald-700' },
  { key: 'request_info', label: 'Request more information', tone: 'bg-amber-600 dark:bg-amber-500 hover:bg-amber-700' },
];

const LINK_MESSAGES = {
  missing_token: 'This link is missing its token. Open the link directly from the approval email.',
  malformed_token: 'This link is not valid.',
  bad_signature: 'This link is not valid.',
  expired: 'This link has expired. Ask the submitter to have the notification resent.',
  wrong_target: 'This link does not match the matter it was issued for.',
  token_revoked: 'This link has already been used or was superseded by a newer notification.',
  step_not_pending: 'This approval has already been decided.',
  not_found: 'This approval could not be found.',
  conflict_result_required: 'Record the conflict-check result before completing this Intake approval.',
};

export default function ApprovalPage() {
  const [params] = useSearchParams();
  const matterId = params.get('matter');
  const stepId = params.get('step');
  const token = params.get('token');

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  // Only relevant when rejectRouting.offerRejectToLastApprover is true (see api.js's
  // approvalContext) -- clicking "Reject" reveals this choice instead of deciding
  // immediately, since there are then two meaningfully different outcomes.
  const [rejectChoiceOpen, setRejectChoiceOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { res, body } = await postJson('/approval/context', { matterId, stepId, token });
      if (!res.ok) {
        setError(LINK_MESSAGES[body.reason] || body.error || 'This link could not be opened.');
        return;
      }
      setData(body);
    } catch (err) {
      setError(err.message || String(err));
    }
  }, [matterId, stepId, token]);

  useEffect(() => {
    if (!matterId || !stepId) {
      setError(LINK_MESSAGES.missing_token);
      return;
    }
    load();
  }, [load, matterId, stepId]);

  const [collectValues, setCollectValues] = useState(null);

  useEffect(() => {
    if (data?.step?.kind === 'data_collection' && collectValues === null) {
      const initial = {};
      (data.step.collectFields || []).forEach((field) => {
        initial[field.path] = field.value ?? (field.type === 'yesno' || field.type === 'checkbox' ? null : '');
      });
      setCollectValues(initial);
    }
  }, [data, collectValues]);

  const submitData = async () => {
    setBusy(true);
    setError('');
    try {
      const { res, body } = await postJson('/approval/decision', {
        matterId,
        stepId,
        token,
        action: 'submit_data',
        values: collectValues,
      });
      if (!res.ok) {
        setError(LINK_MESSAGES[body.reason] || body.message || body.error || 'The information could not be submitted.');
        return;
      }
      setDone({ action: 'submit_data', ...body });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const decide = async (action, rejectTarget) => {
    if (action !== 'approve' && !comment.trim()) {
      setError('Please explain why before rejecting or requesting information.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const { res, body } = await postJson('/approval/decision', {
        matterId,
        stepId,
        token,
        action,
        comment,
        ...(rejectTarget ? { rejectTarget } : {}),
      });
      if (!res.ok) {
        setError(LINK_MESSAGES[body.reason] || body.error || 'The decision could not be recorded.');
        return;
      }
      setDone({ action, rejectTarget, ...body });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRejectClick = () => {
    if (!comment.trim()) {
      setError('Please explain why before rejecting or requesting information.');
      return;
    }
    const offerChoice = data?.rejectRouting?.offerRejectToLastApprover;
    if (offerChoice) {
      setError('');
      setRejectChoiceOpen(true);
      return;
    }
    // Only one meaningful target exists (nothing has been approved yet on this matter) --
    // reject straight to the requestor, same as the app has always done.
    decide('reject', 'requestor');
  };

  if (error && !data) return <Centered><Alert>{error}</Alert></Centered>;
  if (!data) return <Centered><p className="text-sm text-slate-500 dark:text-slate-400">Loading approval…</p></Centered>;

  if (done) {
    const rejectedToLastApprover = done.action === 'reject' && done.rejectTarget === 'last_approver';
    return (
      <Centered>
        <h1 className="text-xl font-semibold text-firm-700 dark:text-firm-100">Thank you</h1>
        <p className="mt-2 text-sm text-slate-700 dark:text-slate-200">
          {done.action === 'submit_data' ? (
            'The information you entered has been recorded.'
          ) : rejectedToLastApprover ? (
            <>
              Your decision (<strong>reject</strong>) has been recorded. The matter was sent back to{' '}
              <strong>{done.reopenedStepLabel || 'the last approver'}</strong> for re-review rather than to the
              requestor.
            </>
          ) : (
            <>
              Your decision (<strong>{done.action.replace('_', ' ')}</strong>) has been recorded.
              {done.matterStatus === 'approved' && ' This was the final approval — the matter is now fully approved.'}
              {done.nextActivated?.length > 0 && ' The next approver has been notified.'}
            </>
          )}
        </p>
        <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">You can close this window.</p>
      </Centered>
    );
  }

  const { matter, step, chain, submitter, canAct, linkStatus, rejectRouting, customFields } = data;

  if (step.kind === 'data_collection') {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-6">
        <header className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Information requested
          </p>
          <h1 className="mt-1 text-xl font-semibold text-firm-700 dark:text-firm-100">{step.label}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {matter.client?.name || 'New matter'} · submitted by {submitter?.displayName || 'a colleague'}
          </p>
          {step.reasons?.length > 0 && (
            <div className="mt-4 rounded border border-firm-100 dark:border-firm-700 bg-firm-50 dark:bg-firm-900 p-3">
              <ul className="list-inside list-disc text-sm text-firm-700 dark:text-firm-100">
                {step.reasons.map((reason, i) => (
                  <li key={i}>{reason.text}</li>
                ))}
              </ul>
            </div>
          )}
        </header>

        {canAct ? (
          <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
            <DataCollectionForm
              fields={step.collectFields}
              values={collectValues || {}}
              onChange={setCollectValues}
              disabled={busy}
            />
            {error && <Alert>{error}</Alert>}
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                disabled={busy || collectValues === null}
                onClick={submitData}
                className="rounded bg-firm-700 dark:bg-firm-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-firm-800 dark:hover:bg-firm-400 disabled:opacity-50"
              >
                {busy ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </section>
        ) : (
          <Alert>{LINK_MESSAGES[linkStatus] || `This request is ${step.status}.`}</Alert>
        )}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-4 p-6">
      <header className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Approval requested
        </p>
        <h1 className="mt-1 text-xl font-semibold text-firm-700 dark:text-firm-100">{step.label}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          {matter.client?.name || 'New matter'} · submitted by {submitter?.displayName || 'a colleague'}
        </p>

        {step.reasons?.length > 0 && (
          <div className="mt-4 rounded border border-firm-100 dark:border-firm-700 bg-firm-50 dark:bg-firm-900 p-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-firm-700 dark:text-firm-100">
              Why your approval is needed
            </p>
            <ul className="mt-1 list-inside list-disc text-sm text-firm-700 dark:text-firm-100">
              {step.reasons.map((reason, i) => (
                <li key={i}>{reason.text}</li>
              ))}
            </ul>
          </div>
        )}
      </header>

      <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Matter summary</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Row label="Client">{matter.client?.name}</Row>
          <Row label="Contact">{matter.client?.contactName}</Row>
          <Row label="Matter name">{matter.filesToBeOpened?.matterName}</Row>
          <Row label="Practice group">{matter.header?.practiceGroupId}</Row>
          <Row label="Matter type">
            {[matter.matterType?.isLitigation && 'Litigation', matter.matterType?.isNonLitigation && 'Non-litigation']
              .filter(Boolean)
              .join(' + ')}
          </Row>
          <Row label="Client source">{matter.clientSource?.source}</Row>
          <Row label="Retainer">
            {matter.header?.retainerAmount != null ? `$${matter.header.retainerAmount}` : '—'}
          </Row>
          <Row label="Engagement letter">
            {matter.header?.engagementLetter?.signedOnFile === true
              ? 'Signed and on file'
              : matter.header?.engagementLetter?.waived
                ? `Waived — ${matter.header.engagementLetter.waiverReason || 'no reason given'}`
                : 'Not on file'}
          </Row>
          <Row label="Areas of law">
            {Array.isArray(matter.areasOfLaw) ? matter.areasOfLaw.join(', ') : matter.areasOfLaw || ''}
          </Row>
          <Row label="Numbers">
            {[matter.clientNumber, matter.matterNumber].filter(Boolean).join(' / ') || 'Not yet assigned'}
          </Row>
        </dl>
      </section>

      <ConflictCheckSummary conflictCheck={matter.conflictCheck} parties={matter.parties} />

      <CustomFieldsSummary customFields={customFields} />

      <AttachmentsSummary attachments={matter.attachments} attachmentLayout={data.attachmentLayout} />

      <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Approval chain</h2>
        <ol className="mt-3 space-y-1 text-sm">
          {chain.map((s) => (
            <li
              key={s.id}
              className={`flex items-center justify-between rounded px-2 py-1 ${
                s.isCurrent ? 'bg-firm-50 dark:bg-firm-900 font-medium text-firm-700 dark:text-firm-100' : 'text-slate-600 dark:text-slate-300'
              }`}
            >
              <span>{s.label}</span>
              <span className="text-xs">
                {s.approverNames.join(' / ') || 'unresolved'} · {s.status}
                {s.status === 'approved' && s.decidedByName ? ` by ${s.decidedByName}` : ''}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {canAct ? (
        <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Comment
            <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
              (required to reject or request information)
            </span>
          </label>
          <textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="mt-1 w-full rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm"
          />
          {error && <Alert>{error}</Alert>}

          {rejectChoiceOpen ? (
            <div className="mt-4 rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3">
              <p className="text-sm font-medium text-red-900 dark:text-red-100">
                {rejectRouting?.lastApprovedByName
                  ? `Send this rejection back to the requestor, or back to ${rejectRouting.lastApprovedByName} (who last approved "${rejectRouting.lastApprovedStepLabel}") for re-review?`
                  : 'Send this rejection back to the requestor, or back to the last approver for re-review?'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => decide('reject', 'requestor')}
                  className="rounded bg-red-600 dark:bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Reject to requestor
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => decide('reject', 'last_approver')}
                  className="rounded bg-red-800 dark:bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-900 disabled:opacity-50"
                >
                  Reject to {rejectRouting?.lastApprovedByName || 'last approver'}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setRejectChoiceOpen(false)}
                  className="rounded border border-slate-300 dark:border-slate-600 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {ACTIONS.map((action) => (
                <button
                  key={action.key}
                  type="button"
                  disabled={busy}
                  onClick={() => decide(action.key)}
                  className={`rounded px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${action.tone}`}
                >
                  {action.label}
                </button>
              ))}
              <button
                type="button"
                disabled={busy}
                onClick={handleRejectClick}
                className="rounded bg-red-600 dark:bg-red-500 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          )}
        </section>
      ) : (
        <Alert>
          {LINK_MESSAGES[linkStatus] || `This approval is ${step.status}.`}
          {step.decidedBy && ` Decided by ${step.decidedBy}.`}
        </Alert>
      )}
    </div>
  );
}

/**
 * Admin-added custom fields, grouped by the page they live on. The server has already
 * decided which fields are active/visible and pre-rendered each value to a display string
 * (see projectCustomFieldsForApprover), so the approver sees the same wording the intake
 * form and the matter-record PDF show. Nothing renders when there are no custom fields.
 */
function CustomFieldsSummary({ customFields }) {
  if (!Array.isArray(customFields) || customFields.length === 0) return null;
  const byPage = [];
  for (const field of customFields) {
    const title = field.sectionTitle || 'Additional information';
    let group = byPage.find((g) => g.title === title);
    if (!group) {
      group = { title, fields: [] };
      byPage.push(group);
    }
    group.fields.push(field);
  }
  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
        Additional information
      </h2>
      {byPage.map((group) => (
        <div key={group.title} className="mt-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {group.title}
          </h3>
          <dl className="mt-1 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {group.fields.map((field, index) => (
              <Row key={`${field.label}-${index}`} label={field.label}>
                {/* Display strings can carry newlines (repeating-group rows); keep them. */}
                <span className="whitespace-pre-line">{field.display}</span>
              </Row>
            ))}
          </dl>
        </div>
      ))}
    </section>
  );
}

/**
 * A `data_collection` step's mini-form: real, editable inputs for exactly the fields the
 * step was configured to collect (`step.collectFields`, from api.js's approvalContext —
 * schema + current value resolved server-side against the effective layout). Deliberately
 * NOT the full intake form's Field.jsx (which is intake-form-state-shaped and expects the
 * whole matter) — this page only ever needs a handful of plain inputs, one per field.
 *
 * v1 supports the same scalar field types Field Builder allows as repeating-group
 * columns (text/textarea/number/money/date/email/tel/yesno/checkbox/select/radio/tags);
 * anything else (repeatable, attachments, directory lookups) is not offered by the admin
 * picker in the first place, so falls back to a plain text input rather than crashing.
 */
function DataCollectionForm({ fields, values, onChange, disabled }) {
  const set = (path, value) => onChange({ ...values, [path]: value });
  const inputClass =
    'mt-1 w-full rounded border border-slate-300 dark:border-slate-600 px-3 py-2 text-sm disabled:bg-slate-100 dark:disabled:bg-slate-800';

  return (
    <div className="space-y-4">
      {(fields || []).map((field) => {
        const value = values[field.path];
        return (
          <label key={field.path} className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            {field.label}
            {field.type === 'textarea' ? (
              <textarea
                rows={3}
                disabled={disabled}
                value={value ?? ''}
                onChange={(e) => set(field.path, e.target.value)}
                className={inputClass}
              />
            ) : field.type === 'yesno' ? (
              <select
                disabled={disabled}
                value={value === true ? 'yes' : value === false ? 'no' : ''}
                onChange={(e) => set(field.path, e.target.value === 'yes' ? true : e.target.value === 'no' ? false : null)}
                className={inputClass}
              >
                <option value="">Select…</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            ) : field.type === 'checkbox' ? (
              <span className="mt-1 flex items-center gap-2 font-normal">
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={value === true}
                  onChange={(e) => set(field.path, e.target.checked)}
                />
                Yes
              </span>
            ) : (field.type === 'select' || field.type === 'radio') && Array.isArray(field.options) ? (
              <select
                disabled={disabled}
                value={value ?? ''}
                onChange={(e) => set(field.path, e.target.value)}
                className={inputClass}
              >
                <option value="">Select…</option>
                {field.options.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            ) : field.type === 'tags' ? (
              <input
                type="text"
                disabled={disabled}
                value={Array.isArray(value) ? value.join(', ') : value ?? ''}
                placeholder="Separate with commas"
                onChange={(e) => set(field.path, e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
                className={inputClass}
              />
            ) : (
              <input
                type={
                  field.type === 'number' || field.type === 'money'
                    ? 'number'
                    : field.type === 'date'
                      ? 'date'
                      : field.type === 'email'
                        ? 'email'
                        : field.type === 'tel'
                          ? 'tel'
                          : 'text'
                }
                disabled={disabled}
                value={value ?? ''}
                onChange={(e) =>
                  set(
                    field.path,
                    (field.type === 'number' || field.type === 'money') && e.target.value !== ''
                      ? Number(e.target.value)
                      : e.target.value
                  )
                }
                className={inputClass}
              />
            )}
          </label>
        );
      })}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
      <dd className="text-slate-800 dark:text-slate-100">{children || '—'}</dd>
    </div>
  );
}

/**
 * Read-only Conflict Check summary for approvers — in particular the Intake team on the
 * new intake_conflict_check gate (see approval/flowDefaults.js), who need to see the raw
 * conflictCheck.* fields captured on the intake form (web/src/lib/formSchema.js's
 * "conflictCheck" section) to actually run the check. The API already sends this data
 * (functions/src/http/api.js's projectMatterForApprover includes `conflictCheck`); this
 * was previously missing from the generic approver view entirely.
 */
function ConflictCheckSummary({ conflictCheck, parties }) {
  const cc = conflictCheck || {};
  const partyList = parties?.list || [];
  const hasAnything =
    partyList.length > 0 ||
    cc.hitsFound != null ||
    cc.hitsAcknowledgedByAttorney === true ||
    cc.clientFullName ||
    cc.entityType ||
    cc.primaryContact ||
    (cc.officersDirectorsMembers || []).length > 0 ||
    (cc.partners || []).length > 0 ||
    cc.ownsOtherBusinesses != null ||
    (cc.otherBusinesses || []).length > 0;
  if (!hasAnything) return null;

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Conflict check</h2>
      {partyList.length > 0 && (
        <div className="mb-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Names and entities for conflict review
          </h3>
          <ul className="mt-1 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700 text-sm">
            {partyList.map((party, i) => (
              <li key={i} className="px-3 py-2">
                <span className="font-medium text-slate-800 dark:text-slate-100">{party.name || 'Unnamed party'}</span>
                {party.entityType && <span className="text-slate-600 dark:text-slate-300"> — {party.entityType}</span>}
                {party.role && <span className="text-slate-500 dark:text-slate-400"> ({party.role})</span>}
                {party.notes && <span className="text-slate-500 dark:text-slate-400"> — {party.notes}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <Row label="Conflict hits found?">
          {cc.hitsFound == null ? undefined : cc.hitsFound ? 'Yes' : 'No'}
        </Row>
        <Row label="Requesting Attorney alerted?">
          {cc.hitsAcknowledgedByAttorney ? 'Yes' : undefined}
        </Row>
        <Row label="Client's full name">{cc.clientFullName}</Row>
        <Row label="Client entity type">{cc.entityType}</Row>
        <Row label="Primary contact">{cc.primaryContact}</Row>
        <Row label="Owns other businesses?">
          {cc.ownsOtherBusinesses == null ? undefined : cc.ownsOtherBusinesses ? 'Yes' : 'No'}
        </Row>
        <Row label="Other business names">{(cc.otherBusinesses || []).join(', ')}</Row>
      </dl>

      {(cc.officersDirectorsMembers || []).length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Officers, directors and members
          </h3>
          <ul className="mt-1 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700 text-sm">
            {cc.officersDirectorsMembers.map((person, i) => (
              <li key={i} className="px-3 py-2">
                <span className="font-medium text-slate-800 dark:text-slate-100">{person.name}</span>
                {person.role && <span className="text-slate-600 dark:text-slate-300"> — {person.role}</span>}
                {(person.relatedBusinessNames || []).length > 0 && (
                  <span className="text-slate-500 dark:text-slate-400">
                    {' '}
                    (related: {person.relatedBusinessNames.join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(cc.partners || []).length > 0 && (
        <div className="mt-4">
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Partners
          </h3>
          <ul className="mt-1 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700 text-sm">
            {cc.partners.map((partner, i) => (
              <li key={i} className="px-3 py-2">
                <span className="font-medium text-slate-800 dark:text-slate-100">{partner.name}</span>
                {partner.isEntity && <span className="text-slate-600 dark:text-slate-300"> (entity)</span>}
                {(partner.relatedBusinessNames || []).length > 0 && (
                  <span className="text-slate-500 dark:text-slate-400">
                    {' '}
                    (related: {partner.relatedBusinessNames.join(', ')})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

/**
 * Read-only attachment listing for approvers, grouped the same way the intake form
 * groups uploads. Approvers need to actually see the conflicts check and engagement
 * letter to make a call — this was previously missing entirely from the approval page.
 */
function AttachmentsSummary({ attachments, attachmentLayout }) {
  const files = Array.isArray(attachments) ? attachments : [];
  if (files.length === 0) return null;
  const categories = attachmentCategoriesForApproval(attachmentLayout, files);

  return (
    <section className="rounded-lg bg-white dark:bg-slate-900 p-6 shadow">
      <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Attachments</h2>
      <div className="mt-3 space-y-4">
        {categories.map(([key, label]) => {
          const group = files.filter((f) => (f.category || 'other') === key);
          if (group.length === 0) return null;
          return (
            <div key={key}>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {label}
              </h3>
              <ul className="mt-1 divide-y divide-slate-100 rounded border border-slate-200 dark:border-slate-700">
                {group.map((file) => (
                  <li
                    key={file.path}
                    className="flex items-center justify-between px-3 py-2 text-sm"
                  >
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-firm-700 dark:text-firm-100 hover:underline"
                    >
                      {file.name}
                    </a>
                    {file.uploadedAt && (
                      <span className="text-xs text-slate-500 dark:text-slate-400" title={file.uploadedAt}>
                        {new Date(file.uploadedAt).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Alert({ children }) {
  return (
    <div className="mt-3 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-3 text-sm text-amber-900 dark:text-amber-100">
      {children}
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-slate-900 p-8 text-center shadow">{children}</div>
    </div>
  );
}
